import React, { useEffect, useState } from "react";
import Label from "../Label/Label";
import { ControlValidationResult } from "../../../models/props";

export type fieldType =
  | "text"
  | "number"
  | "tel"
  | "email"
  | "date"
  | "time"
  | "datetime"
  | "password";

export interface TextBoxProps extends React.ComponentPropsWithoutRef<"input"> {
  text: string;
  validate?: (data: string | number | any) => ControlValidationResult;
  onLostFocus?: (data: string | number | any) => void;
  onChanged?: (value: string | number) => void;
  useFloatingLabels?: boolean;
  step?: number;
  isMultiLine?: boolean;
  maxRows?: number;
  columns?: number;
}

export default function TextBox(props: TextBoxProps) {
  const [textValue, setTextValue] = useState<string | number>("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidEntry, setIsValidEntry] = useState<boolean>(true);

  const setInputValue = (e: HTMLInputElement | HTMLTextAreaElement) => {
    const fieldValue = e.value;
    setValue(fieldValue);

    if (props.validate && e.readOnly === false) {
      const validationResult = props.validate?.(fieldValue);
      setErrorMessage(
        !validationResult?.isValid ? validationResult?.errorMessage : ""
      );

      if (validationResult.isValid) {
        setIsValidEntry(validationResult.isValid);
        setValue(fieldValue);
      }
    }

    if (e.type === "number") {
      if (props.max !== undefined && props.min !== undefined) {
        const maxValue = Number(props.max);
        const minValue = Number(props.min);

        if (Number(fieldValue) > maxValue) {
          setValue(maxValue);
        }

        if (Number(fieldValue) < minValue) {
          setValue(minValue);
          return;
        }
      }
    }
  };

  const setValue = (value: string | number) => {
    setTextValue(value);
    props.onLostFocus?.(value);
  };

  useEffect(() => {
    setTextValue(props.value as string);
  }, [props.value]);

  const renderTextArea = () => {
    return (
      <textarea
        name={props.id}
        id={props.id}
        placeholder={props.placeholder}
        className="form-control"
        rows={props.maxRows}
        cols={props.columns}
        onBlur={(e) => setInputValue(e.currentTarget)}
        title={props.text}
        value={props.value}
      ></textarea>
    );
  };

  const renderTextInput = () => {
    return (
      <div>
        <input
          type={props.type}
          name={props.name}
          value={props.value}
          className={"form-control input " + props.className}
          title={props.text}
          placeholder={props.placeholder}
          min={props.min}
          max={props.max}
          step={props.step}
          disabled={props.disabled}
          readOnly={props.readOnly}
          onBlur={(e) => {
            setInputValue(e.currentTarget);
            props.onChange?.(e);
          }}
          onChange={(e) => {
            setInputValue(e.currentTarget);
            props.onChange?.(e);
          }}
        />

        {(!isValidEntry || errorMessage !== "") && (
          <small>
            <span className="input-error">{errorMessage}</span>
          </small>
        )}
      </div>
    );
  };

  return (
    <div className="form-group">
      {props.text !== "" && <Label name={props.id} text={props.text} />}
      {!props.isMultiLine ? renderTextInput() : renderTextArea()}
    </div>
  );
}
